import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  sharePointDrivesQueryOptions,
  sharePointItemsQueryOptions,
  sharePointSitesQueryOptions,
  type SharePointDrive,
  type SharePointItem,
  type SharePointSite,
} from '@/lib/api/sharepoint.js';

/**
 * The reading half of the SharePoint tree, shared by everything that browses it — the knowledge picker's
 * dialog and the Artifacts section's SharePoint tab. Both walk the same levels over the same endpoints,
 * so they walk them through this module and cannot drift apart.
 */

/**
 * One browse level flattened out of its pages, ready to render. `hasMore` answers the paging question
 * for the whole level.
 */
export interface BrowseList<T> {
  rows: T[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

/**
 * Turns one paged query into a flat list. Two rules the SharePoint contract forces:
 *
 * - A list ends when the service says there is no next page, and at no other point. Graph returns pages
 *   holding zero rows while still carrying a cursor — on one live tenant 15 of 41 drive pages were empty,
 *   and all 26 libraries only appeared because the walk continued to `null`. So `hasMore` is the query's
 *   own answer, never the row count.
 * - An empty page is not something to show the user, so this pulls the next one itself and keeps the
 *   list in its loading state meanwhile. Otherwise the list flashes "No sites found" over a tenant
 *   that has plenty, and "Show more" appears to do nothing.
 *
 * It reads nothing but each page's rows, which is what lets the offset-paged file search share it with the
 * three cursor-paged browse levels.
 */
export function useBrowseList<T>(
  query: UseInfiniteQueryResult<InfiniteData<{ items: T[] }>, Error>,
): BrowseList<T> {
  const { data, hasNextPage, isFetching, isFetchingNextPage, fetchNextPage } =
    query;
  const pageCount = data?.pages.length ?? 0;
  const lastPageIsEmpty =
    pageCount > 0 && data!.pages[pageCount - 1]!.items.length === 0;

  useEffect(() => {
    if (lastPageIsEmpty && hasNextPage && !isFetching) void fetchNextPage();
  }, [lastPageIsEmpty, hasNextPage, isFetching, fetchNextPage, pageCount]);

  const rows = data?.pages.flatMap((page) => page.items) ?? [];
  return {
    rows,
    isLoading: query.isLoading || (rows.length === 0 && hasNextPage),
    isError: query.isError,
    error: query.error,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore: () => void fetchNextPage(),
  };
}

/** The three levels of the tree, each already flattened. */
export interface SharePointBrowseLevels {
  sites: BrowseList<SharePointSite>;
  drives: BrowseList<SharePointDrive>;
  items: BrowseList<SharePointItem>;
}

/**
 * Every level of the tree for one position. Only the level on screen is fetched: the sites call waits
 * until no site is chosen, drives until a site is chosen and no library is, items until a library is.
 * `enabled` is the caller's own gate — the picker keeps it off until its dialog opens.
 */
export function useSharePointLevels({
  workspaceId,
  connectionId = null,
  search = '',
  siteId,
  driveId,
  parent,
  enabled,
}: {
  workspaceId: string;
  /**
   * Which of the workspace's SharePoint connections the tree belongs to. `null` lets the service
   * resolve the workspace's shared connection, which is what a surface showing one tree wants. It is
   * part of every level's cache key, so switching it cannot serve the previous tenant's folders.
   */
  connectionId?: string | null;
  /** Site-name filter for the first level; the empty string lists everything. */
  search?: string;
  siteId: string | null;
  driveId: string | null;
  /** The open folder's `item_id`, or `'root'` for the library's top level. */
  parent: string;
  enabled: boolean;
}): SharePointBrowseLevels {
  const sites = useBrowseList(
    useInfiniteQuery(
      sharePointSitesQueryOptions(
        workspaceId,
        connectionId,
        search,
        enabled && siteId === null,
      ),
    ),
  );
  const drives = useBrowseList(
    useInfiniteQuery(
      sharePointDrivesQueryOptions(
        workspaceId,
        connectionId,
        enabled && siteId !== null && driveId === null ? siteId : null,
      ),
    ),
  );
  const items = useBrowseList(
    useInfiniteQuery(
      sharePointItemsQueryOptions(
        workspaceId,
        connectionId,
        enabled ? driveId : null,
        parent,
      ),
    ),
  );
  return { sites, drives, items };
}
